import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { snippetKeys } from '@/lib/queryKeys'
import { requireAuthToken } from '@/lib/api'

import {
  createSnippet,
  customizeSnippet,
  deleteSnippet,
  fetchSnippet,
  fetchSnippetUsage,
  fetchSnippets,
  patchSnippet,
} from '../api'
import type {
  CreateSnippetInput,
  CustomizeSnippetInput,
  UpdateSnippetInput,
} from '../types'

/** `dutyId` scopes each snippet's `duty_content` to that duty (KD13). */
export function useSnippetsQuery(enabled = true, dutyId?: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: snippetKeys.list(organization?.id, dutyId),
    queryFn: async ({ signal }) =>
      fetchSnippets(await requireAuthToken(getToken), signal, dutyId),
    enabled: enabled && Boolean(organization?.id),
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}

/**
 * Refreshes the snippet caches after a write. Shared by every snippet and
 * variant mutation.
 *
 * The broad sweep goes first and unawaited: `snippetKeys.all` also covers the
 * variants and every other duty's list, and holding `mutateAsync` open until
 * all of them refetch keeps the editor sheet disabled long after the write
 * landed. Only the list the user returns to is awaited, so they close onto
 * fresh data. Awaiting it second joins the refetch the sweep already started.
 */
export function useInvalidateSnippets() {
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return (dutyId?: string) => {
    void queryClient.invalidateQueries({
      queryKey: snippetKeys.all(organization?.id),
    })
    return queryClient.invalidateQueries({
      queryKey: snippetKeys.list(organization?.id, dutyId),
    })
  }
}

export function useCreateSnippetMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async (input: CreateSnippetInput) =>
      createSnippet(await requireAuthToken(getToken), input),
    onSuccess: () => invalidate(),
  })
}

export function useUpdateSnippetMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateSnippetInput & { id: string }) =>
      patchSnippet(await requireAuthToken(getToken), id, input),
    onSuccess: () => invalidate(),
  })
}

export function useDeleteSnippetMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async (id: string) =>
      deleteSnippet(await requireAuthToken(getToken), id),
    onSuccess: () => invalidate(),
  })
}

export function useCustomizeSnippetMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: CustomizeSnippetInput & { id: string }) =>
      customizeSnippet(await requireAuthToken(getToken), id, input),
    onSuccess: () => invalidate(),
  })
}

/**
 * One snippet by id. Used for the Careforce source of an org's copy, which
 * the list does not carry: the copy is listed in the source's place.
 */
export function useSnippetQuery(snippetId: string | null, enabled = true) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: snippetKeys.detail(organization?.id, snippetId ?? undefined),
    queryFn: async ({ signal }) =>
      fetchSnippet(await requireAuthToken(getToken), snippetId!, signal),
    enabled: enabled && Boolean(organization?.id) && Boolean(snippetId),
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}

/** Which orgs read a shared snippet. Careforce only; `enabled` gates the fetch. */
export function useSnippetUsageQuery(snippetId: string | null, enabled = true) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: snippetKeys.usage(organization?.id, snippetId ?? undefined),
    queryFn: async ({ signal }) =>
      fetchSnippetUsage(await requireAuthToken(getToken), snippetId!, signal),
    enabled: enabled && Boolean(organization?.id) && Boolean(snippetId),
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
